
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { triggerIntegrations } from "./use-integration-triggers";

export function useDocumentOperations(documentId: string, field: string, onUpdate: (newContent: string) => void) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  const formatContent = (text: string | null | undefined) => {
    if (!text) return "";
    
    // Remove Markdown characters while preserving the text
    const cleanText = text
      .replace(/[#*]/g, '') // Remove # and * characters
      .replace(/\r\n/g, '\n')
      .replace(/\n\s+/g, '\n')
      .replace(/\s+/g, ' ')
      .trim()
      .split('\n')
      .map(paragraph => paragraph.trim())
      .join('\n\n');

    return cleanText;
  };

  const handleSave = async (newContent: string) => {
    try {
      setIsLoading(true);
      setError(null);
      setProgress(25);
      
      const { data: document } = await supabase
        .from("documents")
        .select("content")
        .eq("id", documentId)
        .single();

      setProgress(50);

      if (!document) throw new Error("Document not found");

      const currentContent = document.content as Record<string, any>;
      const updatedContent = {
        ...currentContent,
        [field]: newContent
      };

      setProgress(75);

      const { error: updateError } = await supabase
        .from("documents")
        .update({ 
          content: updatedContent,
          updated_at: new Date().toISOString()
        })
        .eq("id", documentId);

      if (updateError) throw updateError;

      // Trigger integrations for document updates
      await triggerIntegrations('document_updated', {
        documentId,
        field,
        content: newContent,
        timestamp: new Date().toISOString()
      });

      setProgress(100);
      onUpdate(newContent);
      toast({
        title: "Success",
        description: "Section updated successfully."
      });
      return true;
    } catch (error) {
      console.error("Error updating document:", error);
      setError(error instanceof Error ? error.message : "Failed to update document");
      toast({
        title: "Error",
        description: "Failed to update document. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const handleAIAssist = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setProgress(20);

      // Get user's business assessment for context
      const { data: assessment } = await supabase
        .from('business_assessments')
        .select('data')
        .single();

      setProgress(40);

      // Enhanced prompt with better context
      const { data, error } = await supabase.functions.invoke("generate-content", {
        body: {
          section: field,
          field,
          documentId,
          assessment: assessment?.data || null,
          enhancedContext: true // Flag for better AI responses
        }
      });

      setProgress(70);

      if (error) throw error;
      if (!data?.content) throw new Error("No content generated");

      const formattedContent = formatContent(data.content);
      
      setProgress(90);
      
      const success = await handleSave(formattedContent);
      
      if (success) {
        setProgress(100);
        toast({
          title: "Content Generated",
          description: "AI has generated personalized content for this section based on your business assessment."
        });
        return formattedContent;
      }
      return null;
    } catch (error) {
      console.error("Error generating content:", error);
      setError(error instanceof Error ? error.message : "Failed to generate content");
      toast({
        title: "AI Generation Failed",
        description: "Unable to generate content. Please try again or write manually.",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsLoading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  return {
    isLoading,
    error,
    progress,
    handleSave,
    handleAIAssist,
    formatContent
  };
}
